/**
 * Aplicación web Directorio del Ayuntamiento de Zamora.
 * 
 * Desarrollo e implementación: Jaime León Pereira.
 * Ingeniero Informático de la Unidad TIC.
 */
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import jwtDecode from "jwt-decode";

/**
 * Componente de ruta protegida con autenticación y control de roles.
 * 
 * Wrapper que protege rutas de la aplicación verificando:
 * 1. Autenticación del usuario (redirige a login si no está autenticado)
 * 2. Roles del usuario (redirige a /unauthorized si no tiene el rol requerido)
 *
 */
   
export default function ProtectedRoute({ children, requiredRole, blockMobile = false }) {
  // Estado de autenticación con MSAL
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // Estado de autenticación con MSAL
  const [roles, setRoles] = useState(null);

  /**
   * Efecto principal que gestiona autenticación y carga de roles.
   * Se ejecuta cuando cambia el estado de autenticación de MSAL.
   */
  useEffect(() => {
    // Esperar a que MSAL esté listo
    if (inProgress !== "none") return;

    // Si no está autenticado, redirigir a login
    if (!isAuthenticated) {
      instance.loginRedirect({
        scopes: [process.env.REACT_APP_API_SCOPE],
      });
      return;
    }

    /**
     * Carga los roles del usuario decodificando el token JWT.
     */
    const loadRoles = async () => {
      try {
        // Obtener token de acceso de forma silenciosa
        const response = await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: [process.env.REACT_APP_API_SCOPE],
        });

        // Decodificar token para extraer roles
        const decoded = jwtDecode(response.accessToken);
        setRoles(decoded.roles || []);
      } catch (err) {
        console.error("Error obteniendo roles:", err);
        setRoles([]);
      }
    };

    loadRoles();
  }, [inProgress, isAuthenticated, accounts, instance]);

  // Estados de carga: no renderizar nada mientras se verifica
  if (inProgress !== "none") return null;
  if (!isAuthenticated) return null;
  if (roles === null) return null;

  // Verificar rol requerido
  if (requiredRole && !roles.includes(requiredRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Usuario autenticado y con permisos: renderizar contenido
  return children;
}
